import { TASK_STATUSES, TaskStatus } from '../../types';

/**
 * Filter state for the Browse Tasks admin tool. Every field is a raw string
 * so the form can bind `<input>` / `<select>` directly; empty string means
 * "no filter on this facet".
 *
 * `ownerEmail` is the admin-typed email address. The viewmodel resolves it
 * against the live `users` subscription to get the uid right before calling
 * the tasks query — storing an email here (rather than a uid) means the
 * form binds cleanly to a text box and typos surface as a "no user with
 * that email" error instead of a silent empty result.
 *
 * `ownerUid` is an internal-only escape hatch used by deep links from other
 * admin tools. The Browse Tasks form never asks for it directly, but route
 * params can pass a uid so the search still works when a user has no email
 * on file. Manual edits to the owner-email field should clear it.
 *
 * The tool requires at least one of `taskId`, `ownerEmail`, or `campaignId`
 * before it will hit the server — without one, the underlying Firestore
 * query would need a `(taskType, status, updatedAt)` composite index that
 * doesn't exist today. Keeping the rule in the form preserves the
 * one-query-per-admin-click contract.
 */
export interface BrowseTasksFilter {
  campaignId: string;
  taskTypeId: string;
  status: TaskStatus | '';
  ownerEmail: string;
  ownerUid: string;
  taskId: string;
}

export const EMPTY_BROWSE_FILTER: BrowseTasksFilter = {
  campaignId: '',
  taskTypeId: '',
  status: '',
  ownerEmail: '',
  ownerUid: '',
  taskId: '',
};

/**
 * Returns a user-facing error string when the filter isn't runnable, or
 * null when it is. A taskId short-circuits all other requirements — direct
 * doc lookups don't need a primary facet.
 */
export function validateBrowseFilter(f: BrowseTasksFilter): string | null {
  if ((f.taskId ?? '').trim()) return null;
  if ((f.ownerEmail ?? '').trim() || (f.ownerUid ?? '').trim() || f.campaignId) return null;
  return 'Pick a campaign, enter an owner email, or paste a task ID.';
}

/** Filter is runnable when `validateBrowseFilter` returns null. */
export function canRunBrowseFilter(f: BrowseTasksFilter): boolean {
  return validateBrowseFilter(f) === null;
}

export interface BrowseTasksRouteState {
  filter: BrowseTasksFilter;
  autoRun: boolean;
}

export function readBrowseTasksSearchParams(
  params: URLSearchParams,
): BrowseTasksRouteState | null {
  const filter: BrowseTasksFilter = {
    campaignId: params.get('campaignId')?.trim() ?? '',
    taskTypeId: params.get('taskTypeId')?.trim() ?? '',
    status: parseTaskStatus(params.get('status')),
    ownerEmail: params.get('ownerEmail')?.trim() ?? '',
    ownerUid: params.get('ownerUid')?.trim() ?? '',
    taskId: params.get('taskId')?.trim() ?? '',
  };
  const autoRun = params.get('autorun') === '1';
  if (
    !filter.campaignId
    && !filter.taskTypeId
    && !filter.status
    && !filter.ownerEmail
    && !filter.ownerUid
    && !filter.taskId
  ) {
    return null;
  }
  return { filter, autoRun };
}

export function buildBrowseTasksSearchParams(
  input: Partial<BrowseTasksFilter> & { autorun?: boolean },
): string {
  const params = new URLSearchParams();
  const setIfPresent = (key: string, value: string | undefined) => {
    const trimmed = value?.trim();
    if (trimmed) params.set(key, trimmed);
  };

  setIfPresent('campaignId', input.campaignId);
  setIfPresent('taskTypeId', input.taskTypeId);
  setIfPresent('status', input.status);
  setIfPresent('ownerEmail', input.ownerEmail);
  setIfPresent('ownerUid', input.ownerUid);
  setIfPresent('taskId', input.taskId);
  if (input.autorun) params.set('autorun', '1');

  return params.toString();
}

function parseTaskStatus(value: string | null): TaskStatus | '' {
  const status = value?.trim() ?? '';
  return TASK_STATUSES.includes(status as TaskStatus) ? status as TaskStatus : '';
}
